// Story 5.3 — "List for sale" dialog for a sealed (unopened) DOSSIER, launched
// from the vault grid. Thin wrapper over the shared <ListingDialog> (all UI +
// form/KYC/error logic lives there) — this file only wires the sealed-dossier-
// specific hooks + config, which diverge from the certificate variant in:
//   * Floor price bucketed by drop_id (dossiers from the same drop are
//     fungible), not creator + tier.
//   * €1.00 minimum (FR42 vs the share's FR41 €0.10).
//   * create-sealed-dossier-listing mutation + ["vault-items"] invalidation.
//   * Undo toast kind: "sealed_dossier".
//   * A retirement/scarcity context line rendered as calm, factual prose.
//     ⚠️ Amended 2026-06-13 (MiFID II legal-defensive decision #1): the
//     original line predicted appreciation ("sealed dossiers tend to
//     appreciate after the drop retires"). That framed holding as yielding a
//     return — investment-signal language. Replaced with a factual scarcity
//     statement + an explicit no-guarantee disclosure.

import { useQueryClient } from "@tanstack/react-query";

import { SEALED_DOSSIER_LISTING_PRICE_MIN_CENTS } from "./feeMath";
import { ListingDialog } from "./ListingDialog";
import { showUndoListingToast } from "./showUndoListingToast";
import { useCreateSealedDossierListing } from "./useCreateSealedDossierListing";
import { useSealedDossierDropFloorPrice } from "./useSealedDossierDropFloorPrice";

export interface ListSealedDossierForSaleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sealedDossierId: string;
  dropId: string;
}

export function ListSealedDossierForSaleDialog({
  open,
  onOpenChange,
  sealedDossierId,
  dropId,
}: ListSealedDossierForSaleDialogProps) {
  const queryClient = useQueryClient();
  const floorPriceQuery = useSealedDossierDropFloorPrice({
    dropId,
    enabled: open,
  });
  const mutation = useCreateSealedDossierListing();

  return (
    <ListingDialog
      open={open}
      onOpenChange={onOpenChange}
      itemNoun="sealed dossier"
      idPrefix="list-sd"
      minCents={SEALED_DOSSIER_LISTING_PRICE_MIN_CENTS}
      floorScopeLabel="drop"
      scarcityNote={
        <>
          Once a drop retires, no new sealed dossiers from it are issued (like a
          retired CS:GO case). Any resale price is only ever what another
          collector will pay — never guaranteed.
        </>
      }
      alreadyExistsMessage="This sealed dossier already has an active listing. Refresh to see current state."
      floorPriceCents={floorPriceQuery.data?.floor_price_cents}
      isSubmitting={mutation.isPending}
      createListing={(args) =>
        mutation.mutateAsync({ sealed_dossier_id: sealedDossierId, ...args })
      }
      onListed={(listingId) => {
        showUndoListingToast({
          kind: "sealed_dossier",
          listingId,
          invalidationKeys: [["vault-items"]],
          queryClient,
        });
        void queryClient.invalidateQueries({ queryKey: ["vault-items"] });
      }}
      onAlreadyExists={() =>
        void queryClient.invalidateQueries({ queryKey: ["vault-items"] })
      }
    />
  );
}
